<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ isEdit ? t("receipts.edit_title") : t("receipts.add_title") }}
            </h6>
          </div>
        </div>
      </div>
      <div class="card-body px-0 pb-2 pe-3 position-relative">
        <!-- Mode tabs (only in create mode) -->
        <div v-if="!isEdit" class="d-flex gap-2 mx-3 mb-3">
          <button
            type="button"
            class="btn btn-sm"
            :class="mode === 'single' ? 'btn-primary' : 'btn-outline-primary'"
            @click="mode = 'single'"
          >
            <i class="fas fa-user me-1"></i>{{ t("receipts.mode_single") }}
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="mode === 'distribute' ? 'btn-primary' : 'btn-outline-primary'"
            @click="mode = 'distribute'"
          >
            <i class="fas fa-users me-1"></i>{{ t("receipts.mode_distribute") }}
          </button>
        </div>

        <div class="row justify-content-center">
          <div class="col-md-10 ms-3">

            <!-- ══════════════ DISTRIBUTE MODE ══════════════ -->
            <div v-if="mode === 'distribute' && !isEdit" key="distribute-mode">
              <div class="row g-3">
                <!-- Guardian selector (same as single) -->
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.guardian_label") }}</label>
                  <input
                    type="text"
                    v-model="guardianSearch"
                    class="form-control borderless-input"
                    :placeholder="t('receipts.guardian_placeholder')"
                    @input="filterGuardians"
                  />
                  <div v-if="filteredGuardians.length > 0 && guardianSearch" class="list-group guardian-search-results">
                    <a v-for="g in filteredGuardians" :key="g.id" href="#"
                       class="list-group-item list-group-item-action"
                       @click.prevent="selectGuardian(g)">
                      {{ g.username }} - {{ g.phone_num }}
                      <span v-if="g.is_primary" class="badge bg-success ms-2">{{ t("receipts.primary") }}</span>
                    </a>
                  </div>
                </div>

                <!-- Academic year -->
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.academic_year") }}</label>
                  <select v-model="form.academic_year_id" class="form-select borderless-input" @change="onDistributeYearChange">
                    <option value="" disabled>{{ t("receipts.select_academic_year") }}</option>
                    <option v-for="year in academicYears" :value="year.id" :key="year.id">
                      {{ year.name }} ({{ year.year }})<span v-if="year.is_active"> - {{ t("common.current") }}</span>
                    </option>
                  </select>
                </div>

                <!-- Children fee table -->
                <div v-if="distributeChildren.length > 0" class="col-12">
                  <div class="card bg-light mb-2">
                    <div class="card-body p-2">
                      <h6 class="mb-2"><i class="fas fa-children me-2"></i>{{ t("receipts.children_fee_summary") }}</h6>
                      <div class="table-responsive">
                        <table class="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th>{{ t("receipts.student") }}</th>
                              <th>{{ t("receipts.class_fee") }}</th>
                              <th>{{ t("receipts.total_paid") }}</th>
                              <th>{{ t("receipts.current_balance") }}</th>
                              <th class="text-success">{{ t("receipts.will_receive") }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="child in distributeChildren" :key="child.student_id"
                                :class="child.will_pay > 0 ? '' : 'text-muted'">
                              <td>{{ child.student_name }}</td>
                              <td>{{ formatCurrency(child.class_fee) }}</td>
                              <td>
                                {{ formatCurrency(child.paid) }}
                                <div v-if="child.discount_given > 0" class="small text-muted">
                                  {{ t("receipts.paid_breakdown", {
                                    cash: formatCurrency(child.cash_paid),
                                    discount: formatCurrency(child.discount_given),
                                    percentage: child.discount_percentage ?? '-'
                                  }) }}
                                </div>
                              </td>
                              <td>{{ formatCurrency(child.remaining) }}</td>
                              <td class="fw-bold" :class="child.will_pay > 0 ? 'text-success' : 'text-muted'">
                                {{ formatCurrency(child.will_pay) }}
                              </td>
                            </tr>
                            <tr class="fw-bold border-top">
                              <td>{{ t("receipts.total") }}</td>
                              <td></td>
                              <td></td>
                              <td>{{ formatCurrency(distributeTotalRemaining) }}</td>
                              <td class="text-success">{{ formatCurrency(distributeTotalWillPay) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Warning: amount > total remaining -->
                <div v-if="distributeExceedsWarning" class="col-12">
                  <div class="alert alert-warning d-flex align-items-center gap-2">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>
                      {{ t("receipts.distribute_exceeds_warning", { remaining: formatCurrency(distributeTotalRemaining) }) }}
                      {{ t("receipts.distribute_will_pay_only") }}
                    </span>
                  </div>
                </div>

                <!-- Amount input -->
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.amount_to_distribute") }}</label>
                  <div class="input-group">
                    <span class="input-group-text">{{ t("receipts.lyd_abbr") }}</span>
                    <input type="number"
                           :value="distributeAmount"
                           @input="distributeAmount = $event.target.value"
                           class="form-control borderless-input"
                           min="1" step="1"
                           :disabled="!form.guardian_id || !form.academic_year_id || loadingDistribute" />
                    <button type="button" class="btn btn-outline-secondary"
                            @click="distributeAmount = String(distributeTotalRemaining)"
                            :disabled="distributeTotalRemaining <= 0">
                      {{ t("receipts.pay_full") }}
                    </button>
                  </div>
                  <div v-if="distributeAmountError" class="text-danger small mt-1">{{ distributeAmountError }}</div>
                </div>

                <!-- Wallet balance -->
                <div class="col-md-6" v-if="form.guardian_id">
                  <label class="bmd-label-float">{{ t("receipts.wallet_balance") }}</label>
                  <div class="alert mb-0" :class="walletBalance >= distributeTotalWillPay ? 'alert-success' : 'alert-danger'">
                    <strong>{{ t("receipts.wallet_balance") }}:</strong> {{ formatCurrency(walletBalance) }}
                    <span v-if="walletBalance < distributeTotalWillPay" class="ms-2 text-danger">
                      ({{ t("receipts.insufficient_balance") }})
                    </span>
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.payment_date") }}</label>
                  <input type="date" v-model="form.payment_date"
                         class="form-control borderless-input" />
                </div>

                <!-- Notes -->
                <div class="col-12">
                  <label class="bmd-label-float">{{ t("receipts.notes") }}</label>
                  <textarea v-model="form.notes" class="form-control borderless-input" rows="2"
                            :placeholder="t('receipts.notes_placeholder')"></textarea>
                </div>

                <!-- Submit -->
                <div class="col-12 mt-4">
                  <div class="d-flex gap-3">
                    <button type="button" class="btn btn-primary flex-grow-1"
                            :disabled="isSubmitting || !distributeCanSubmit"
                            @click="submitDistribute">
                      <span v-if="isSubmitting">
                        <i class="fas fa-spinner fa-spin me-2"></i>{{ t("receipts.saving") }}
                      </span>
                      <span v-else>
                        <i class="fas fa-share-alt ms-2"></i>{{ t("receipts.distribute_submit") }}
                      </span>
                    </button>
                    <router-link :to="{ name: 'payment-receipts.index' }" class="btn btn-outline-secondary flex-grow-1">
                      <i class="fas fa-times ms-2"></i>{{ t("receipts.cancel") }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- ══════════════ SINGLE STUDENT MODE ══════════════ -->
            <div v-else key="single-mode">
            <form @submit.prevent="submitForm" @keydown.enter.prevent>
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.guardian_label") }}</label>
                  <input
                    type="text"
                    v-model="guardianSearch"
                    class="form-control borderless-input"
                    :placeholder="t('receipts.guardian_placeholder')"
                    @input="filterGuardians"
                    :disabled="loadingStudents || isEdit"
                  />
                  <div
                    v-if="filteredGuardians.length > 0 && guardianSearch"
                    class="list-group guardian-search-results"
                  >
                    <a
                      v-for="guardian in filteredGuardians"
                      :key="guardian.id"
                      href="#"
                      class="list-group-item list-group-item-action"
                      @click.prevent="selectGuardian(guardian)"
                    >
                      {{ guardian.username }} - {{ guardian.phone_num }}
                      <span
                        v-if="guardian.is_primary"
                        class="badge bg-success ms-2"
                        >{{ t("receipts.primary") }}</span
                      >
                    </a>
                  </div>
                  <input
                    type="hidden"
                    v-model="form.guardian_id"
                    :class="{ 'is-invalid': errors.guardian_id }"
                    required
                    :disabled="isEdit"
                  />
                  <div class="invalid-feedback">{{ errors.guardian_id }}</div>
                </div>

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.student") }}</label>
                  <select
                    v-model="form.student_id"
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.student_id }"
                    required
                    :disabled="loadingStudents || !form.guardian_id || isEdit"
                    @change="onStudentChange"
                  >
                    <option value="" disabled>
                      {{ t("receipts.select_student") }}
                    </option>
                    <option v-if="loadingStudents" value="" disabled>
                      {{ t("receipts.loading_students") }}
                    </option>
                    <option
                      v-for="student in students"
                      :value="student.id"
                      :key="student.id"
                    >
                      {{ student.first_name }} {{ student.father_name }}
                      {{ student.last_name }}
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ errors.student_id }}</div>
                </div>

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.academic_year") }}</label>
                  <select
                    v-model="form.academic_year_id"
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.academic_year_id }"
                    required
                    :disabled="isEdit"
                    @change="onAcademicYearChange"
                  >
                    <option value="" disabled>
                      {{ t("receipts.select_academic_year") }}
                    </option>
                    <option
                      v-for="year in academicYears"
                      :value="year.id"
                      :key="year.id"
                      :class="{ 'fw-bold text-success': year.is_active }"
                    >
                      {{ year.name }} ({{ year.year }})
                      <span v-if="year.is_active">
                        - {{ t("common.current") }}
                      </span>
                    </option>
                  </select>
                  <div class="invalid-feedback">
                    {{ errors.academic_year_id }}
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="mt-4">
                    <span v-if="activeAcademicYear && form.academic_year_id === activeAcademicYear.id" 
                          class="badge bg-success">
                      <i class="fas fa-check-circle me-1"></i>
                      {{ t("receipts.active_year_selected") }}
                    </span>
                    <span v-else-if="form.academic_year_id" 
                          class="badge bg-warning">
                      <i class="fas fa-exclamation-triangle me-1"></i>
                      {{ t("receipts.inactive_year_selected") }}
                    </span>
                  </div>
                </div>

                <div v-if="form.student_id && form.academic_year_id" class="col-12">
                  <div class="card bg-light mb-3">
                    <div class="card-body">
                      <h6 class="card-title mb-3">
                        <i class="fas fa-money-bill-wave me-2"></i>
                        {{ t("receipts.fee_summary_for_year") }}: 
                        <strong>{{ selectedAcademicYear?.name }}</strong>
                      </h6>
                      <div class="row text-center">
                        <div class="col-md-4">
                          <h6 class="text-muted">
                            {{ t("receipts.class_fee") }}
                          </h6>
                          <h4>
                            {{ formatCurrency(studentFeeStatus.class_fee) }}
                          </h4>
                          <small class="text-muted">
                            {{ t("receipts.class") }}: {{ studentFeeStatus.enrollment?.class_name || 'N/A' }}
                          </small>
                        </div>
                        <div class="col-md-4">
                          <h6 class="text-muted">
                            {{ t("receipts.total_paid") }}
                          </h6>
                          <h4
                            :class="{
                              'text-danger': studentFeeStatus.paid < 0,
                              'text-success': studentFeeStatus.paid >= 0
                            }"
                          >
                            {{ formatCurrency(studentFeeStatus.paid) }}
                          </h4>
                          <small class="text-muted">
                            {{ t("receipts.in_selected_year") }}
                          </small>
                          <div v-if="studentFeeStatus.discount_given > 0" class="small mt-1">
                            <span class="badge bg-light text-dark border">
                              {{ t("receipts.paid_breakdown", {
                                cash: formatCurrency(studentFeeStatus.cash_paid),
                                discount: formatCurrency(studentFeeStatus.discount_given),
                                percentage: studentFeeStatus.discount_percentage ?? '-'
                              }) }}
                            </span>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <h6 class="text-muted">
                            {{ t("receipts.current_balance") }}
                          </h6>
                          <h4
                            :class="{
                              'text-danger': studentFeeStatus.remaining < 0,
                              'text-success': studentFeeStatus.remaining > 0,
                              'text-secondary': studentFeeStatus.remaining === 0
                            }"
                          >
                            {{ formatCurrency(studentFeeStatus.remaining) }}
                            <small v-if="studentFeeStatus.remaining < 0">
                              ({{ t("receipts.debtor") }})
                            </small>
                            <small v-else-if="studentFeeStatus.remaining > 0">
                              ({{ t("receipts.creditor") }})
                            </small>
                            <small v-else>
                              ({{ t("receipts.paid_full") }})
                            </small>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Reminder: a discount was already used for this student this year -->
                <div class="col-12" v-if="studentFeeStatus.existing_discount">
                  <div class="alert alert-info d-flex align-items-center gap-2 mb-0">
                    <i class="fas fa-tag"></i>
                    <span>
                      {{
                        t("receipts.existing_discount_hint", {
                          percentage: studentFeeStatus.existing_discount.discount_percentage,
                          receipt: studentFeeStatus.existing_discount.receipt_number,
                        })
                      }}
                    </span>
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.amount") }}</label>
                  <div class="input-group">
                    <span class="input-group-text">{{ t("receipts.lyd_abbr") }}</span>
                    <input
                      type="number"
                      v-model="form.amount"
                      class="form-control borderless-input"
                      :max="isEdit ? null : studentFeeStatus.remaining"
                      required
                      step="1"
                      :disabled="!form.student_id || !form.academic_year_id"
                      @input="validateAmount"
                    />
                    <button
                      v-if="!isEdit && form.student_id && form.academic_year_id"
                      type="button"
                      class="btn btn-outline-secondary"
                      @click="setFullAmount"
                    >
                      {{ t("receipts.pay_full") }}
                    </button>
                  </div>
                  <div v-if="amountError" class="text-danger small mt-1">
                    {{ amountError }}
                  </div>
                  <div v-if="studentFeeStatus.remaining > 0 && !isEdit" class="text-info small mt-1">
                    {{ t("receipts.max_allowed") }}: {{ formatCurrency(studentFeeStatus.remaining) }}
                  </div>
                </div>

                <div class="col-md-6" v-if="form.guardian_id">
                  <label class="bmd-label-float">{{ t("receipts.wallet_balance") }}</label>
                  <div class="alert" :class="walletStatusClass">
                    <strong>{{ t("receipts.wallet_balance") }}:</strong>
                    {{ formatCurrency(walletBalance) }}
                    <span v-if="showInsufficientWarning" class="ms-2">
                      ({{ insufficientMessage }})
                    </span>
                    <span v-else-if="isEdit && amountChange < 0" class="ms-2 text-success">
                      ({{ t("receipts.refund_message", { amount: formatCurrency(Math.abs(amountChange)) }) }})
                    </span>
                  </div>
                </div>

                <!-- Check number — shown only when bank_check selected -->
                <div class="col-md-6" v-if="form.payment_method === 'bank_check'">
                  <label class="bmd-label-float">{{ t("receipts.check_number") }}</label>
                  <input
                    type="text"
                    v-model.trim="form.check_number"
                    class="form-control borderless-input"
                    maxlength="100"
                  />
                </div>

                <!-- Payment date -->
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t("receipts.payment_date") }}</label>
                  <input
                    type="date"
                    v-model="form.payment_date"
                    class="form-control borderless-input"
                  />
                </div>

                <div class="col-12">
                  <label class="bmd-label-float">{{ t("receipts.notes") }}</label>
                  <textarea
                    v-model="form.notes"
                    class="form-control borderless-input"
                    rows="2"
                    :placeholder="t('receipts.notes_placeholder')"
                  ></textarea>
                </div>

                <div class="col-12" v-if="studentFeeStatus.enrollment && form.student_id">
                  <div class="alert alert-info">
                    <i class="fas fa-info-circle me-2"></i>
                    <strong>{{ t("receipts.enrollment_info") }}:</strong>
                    {{ t("receipts.enrolled_in") }} 
                    <strong>{{ studentFeeStatus.enrollment.class_name || 'N/A' }}</strong>
                    {{ t("receipts.for_academic_year") }} 
                    <strong>{{ selectedAcademicYear?.name || 'N/A' }}</strong>
                  </div>
                </div>

                <div class="col-12 mt-4">
                  <div class="d-flex gap-3">
                    <button
                      type="submit"
                      class="btn btn-primary flex-grow-1"
                      :disabled="isSubmitting || hasValidationErrors || !form.student_id || !form.academic_year_id"
                    >
                      <span v-if="isSubmitting">
                        <i class="fas fa-spinner fa-spin me-2"></i>
                        {{ t("receipts.saving") }}
                      </span>
                      <span v-else>
                        <i class="fas fa-save ms-2"></i>
                        {{ isEdit ? t("receipts.save_edit") : t("receipts.save") }}
                      </span>
                    </button>
                    <router-link
                      :to="{ name: 'payment-receipts.index' }"
                      class="btn btn-outline-secondary flex-grow-1"
                    >
                      <i class="fas fa-times ms-2"></i>
                      {{ t("receipts.cancel") }}
                    </router-link>
                  </div>
                </div>
              </div>
            </form>
            </div><!-- end single-student wrapper -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import api from "@/api";

const { t, locale } = useI18n();
const toast = useToast();
const route = useRoute();
const router = useRouter();

// ── Mode toggle ────────────────────────────────────────────────────────────
const mode = ref("single"); // 'single' | 'distribute'

// ── Distribute-mode state ──────────────────────────────────────────────────
const distributeAmount   = ref(null); // always a string from the input
const distributeChildren = ref([]);  // [{ student_id, student_name, class_fee, paid, remaining, will_pay }]
const loadingDistribute  = ref(false);
const distributeAmountError = ref("");

// ── Cent-safe helpers ────────────────────────────────────────────────────────
// All distribution arithmetic is done in integer "millimes" (×100) to avoid
// JavaScript floating-point drift (e.g. 3000 becoming 2999.99).
const toCents   = (v) => Math.round(parseFloat(v) || 0);
const fromCents = (c) => c;

const distributeTotalRemaining = computed(() => {
  const cents = distributeChildren.value.reduce((s, c) => s + toCents(c.remaining), 0);
  return fromCents(cents);
});

// Total that will actually be paid = sum of will_pay already computed by buildDistributePlan.
// We read will_pay (not remaining) so this always matches what the user sees in the table.
const distributeTotalWillPay = computed(() =>
  fromCents(distributeChildren.value.reduce((s, c) => s + toCents(c.will_pay || 0), 0))
);

// ── Equal-split distribution ──────────────────────────────────────────────────
// Algorithm:
//   Round 1 – divide pool evenly (floor) among all children who still have remaining.
//             Any leftover cents (from floor) go 1-by-1 to the first children.
//   If a child's remaining < their share, they get capped and the overflow
//   goes back into the pool → next round splits the overflow among those still able to receive.
//   Repeat until pool is 0 or everyone is capped.
// All arithmetic is in integer cents to prevent floating-point drift.
const buildDistributePlan = () => {
  const totalCents = toCents(distributeAmount.value);

  if (totalCents <= 0) {
    distributeChildren.value = distributeChildren.value.map(c => ({ ...c, will_pay: 0 }));
    distributeAmountError.value = t("receipts.amount_required");
    return;
  }
  distributeAmountError.value = "";

  // Build a mutable working copy (integer cents).
  const items = distributeChildren.value.map(c => ({
    remCents:  toCents(c.remaining),
    willCents: 0,
  }));

  let pool   = totalCents;
  let active = items.map((_, i) => i).filter(i => items[i].remCents > 0);

  while (pool > 0 && active.length > 0) {
    const share    = Math.floor(pool / active.length);
    const extraFor = pool % active.length; // distribute leftover cents 1-by-1

    let overflow      = 0;
    const nextActive  = [];

    active.forEach((i, pos) => {
      const extra   = pos < extraFor ? 1 : 0;
      const canTake = items[i].remCents - items[i].willCents;
      const give    = Math.min(canTake, share + extra);
      items[i].willCents += give;
      overflow           += (share + extra - give); // capped → overflow back
      if (items[i].willCents < items[i].remCents) nextActive.push(i);
    });

    pool   = overflow;
    active = nextActive;
  }

  distributeChildren.value = distributeChildren.value.map((c, i) => ({
    ...c,
    will_pay: fromCents(items[i].willCents),
  }));
};

watch(distributeAmount, buildDistributePlan);

const distributeExceedsWarning = computed(() => {
  const amountCents    = toCents(distributeAmount.value);
  const remainingCents = toCents(distributeTotalRemaining.value);
  return amountCents > remainingCents && remainingCents > 0;
});

const distributeCanSubmit = computed(() => {
  const amount = parseFloat(distributeAmount.value) || 0;
  return (
    form.value.guardian_id &&
    form.value.academic_year_id &&
    distributeChildren.value.length > 0 &&
    amount > 0 &&
    !distributeAmountError.value &&
    walletBalance.value >= distributeTotalWillPay.value
  );
});

const loadDistributeChildren = async () => {
  if (!form.value.guardian_id || !form.value.academic_year_id) {
    distributeChildren.value = [];
    distributeAmount.value = null;
    return;
  }
  loadingDistribute.value = true;
  try {
    const studentsRes = await api.get(`/guardians/${form.value.guardian_id}/students`);
    const kids = studentsRes.data.data || [];

    const statuses = await Promise.all(
      kids.map(k =>
        api.get(`/payment-receipts/student-fee-status/${k.id}`, {
          params: { academic_year_id: form.value.academic_year_id }
        }).then(r => ({
          student_id:   k.id,
          student_name: `${k.first_name} ${k.father_name} ${k.last_name}`,
          class_fee:    fromCents(toCents(r.data.class_fee  || 0)),
          paid:         fromCents(toCents(r.data.paid       || 0)),
          cash_paid:      fromCents(toCents(r.data.cash_paid || 0)),
          discount_given: fromCents(toCents(r.data.discount_given || 0)),
          discount_percentage: r.data.discount_percentage ?? null,
          remaining:    fromCents(Math.max(0, toCents(r.data.remaining || 0))),
          class_name:   r.data.enrollment?.class_name || null,
          will_pay:     0,
        }))
      )
    );
    distributeChildren.value = statuses;
    buildDistributePlan();
  } catch (e) {
    console.error("Error loading distribute children:", e);
    distributeChildren.value = [];
  } finally {
    loadingDistribute.value = false;
  }
};

const onDistributeYearChange = () => {
  selectedAcademicYear.value = academicYears.value.find(y => y.id === form.value.academic_year_id);
  loadDistributeChildren();
};

// NOTE: watches that depend on `form` are registered after `form` is declared below.

const submitDistribute = async () => {
  if (!distributeCanSubmit.value) return;
  isSubmitting.value = true;
  try {
    const amount = parseFloat(distributeAmount.value);
    const res = await api.post("/payment-receipts/distribute", {
      guardian_id:      form.value.guardian_id,
      academic_year_id: form.value.academic_year_id,
      amount,
      notes:            form.value.notes || null,
      payment_method:   form.value.payment_method   || null,
      check_number:     form.value.check_number     || null,
      payment_date:     form.value.payment_date     || null,
    });
    toast.success(res.data.message || t("receipts.add_success"));
    router.push({ name: "payment-receipts.index" });
  } catch (error) {
    if (error?.response?.data?.code === "AMOUNT_EXCEEDS_REMAINING") {
      // Server already warned via frontend computed — shouldn't normally reach here
      toast.warning(error.response.data.message);
    } else {
      const msg = error?.response?.data?.message || t("receipts.save_failed");
      toast.error(msg);
    }
  } finally {
    isSubmitting.value = false;
  }
};

const isSubmitting = ref(false);
const errors = ref({});
const guardians = ref([]);
const filteredGuardians = ref([]);
const guardianSearch = ref("");
const students = ref([]);
const loadingStudents = ref(false);
const amountError = ref("");
const academicYears = ref([]);
const selectedAcademicYear = ref(null);

const isEdit = computed(() => route.name === "payment-receipts.edit");

const form = ref({
  guardian_id:      route.query.guardian_id || "",
  student_id:       route.query.student_id  || "",
  amount:           route.query.amount || 0,
  notes:            route.query.notes || "",
  academic_year_id: "",
  payment_method:   "",
  check_number:     "",
  payment_date:     new Date().toISOString().split('T')[0],
});

// Watchers that reference `form` — must be after `form` declaration.
watch(
  () => form.value.guardian_id,
  () => { if (mode.value === "distribute") loadDistributeChildren(); }
);
watch(mode, (m) => {
  if (m === "distribute") loadDistributeChildren();
});

const walletBalance = ref(0);
const studentFeeStatus = ref({
  class_fee: 0,
  paid: 0,
  remaining: 0,
  enrollment: null,
  existing_discount: null,
});
const originalAmount = ref(0);

const amountChange = computed(() => {
  return parseFloat(form.value.amount) - originalAmount.value;
});

const showInsufficientWarning = computed(() => {
  if (isEdit.value) {
    return amountChange.value > 0 && walletBalance.value < amountChange.value;
  } else {
    return walletBalance.value < form.value.amount;
  }
});

const insufficientMessage = computed(() => {
  if (isEdit.value) {
    return t("receipts.insufficient_balance_for_increase");
  } else {
    return t("receipts.insufficient_balance");
  }
});

const activeAcademicYear = computed(() =>
  academicYears.value.find((y) => y.is_active)
);

const walletStatusClass = computed(() => {
  if (isEdit.value) {
    if (amountChange.value > 0) {
      return walletBalance.value >= amountChange.value
        ? "alert-success"
        : "alert-danger";
    } else {
      return "alert-success";
    }
  } else {
    return walletBalance.value >= form.value.amount
      ? "alert-success"
      : "alert-danger";
  }
});

const hasValidationErrors = computed(() => {
  return !!amountError.value || showInsufficientWarning.value;
});

function extractApiMessage(error, fallbackKey = "errors.generic") {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === "object" && data.message !== null) {
      const loc = (data.locale || locale.value || "en")
        .toLowerCase()
        .startsWith("ar")
        ? "ar"
        : "en";
      if (typeof data.message[loc] === "string") return data.message[loc];
    }
    if (typeof data.message === "string") return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = "errors.generic") {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t("auth.unauthorized", "Unauthorized"));
  if (s === 403) return toast.error(t("auth.forbidden", "Forbidden"));
  return toast.error(extractApiMessage(error, fallbackKey));
}

const loadAcademicYears = async () => {
  try {
    const response = await api.get("/academic-years");
    academicYears.value = response.data.data || [];
    
    if (!isEdit.value && !form.value.academic_year_id) {
      const currentYear = academicYears.value.find((y) => y.is_active);
      if (currentYear) {
        form.value.academic_year_id = currentYear.id;
        selectedAcademicYear.value = currentYear;
      }
    } else if (form.value.academic_year_id) {
      selectedAcademicYear.value = academicYears.value.find(y => y.id === form.value.academic_year_id);
    }
  } catch (error) {
    console.error("Error loading academic years:", error);
  }
};

const fetchStudentFeeStatus = async (studentId, academicYearId) => {
  if (!studentId || !academicYearId) {
    studentFeeStatus.value = { class_fee: 0, paid: 0, remaining: 0, enrollment: null, existing_discount: null };
    return;
  }
  
  try {
    const res = await api.get(
      `/payment-receipts/student-fee-status/${studentId}`,
      {
        params: {
          academic_year_id: academicYearId
        }
      }
    );
    
    if (res.data) {
      studentFeeStatus.value = res.data;
      
      // تحديث المبلغ المسموح به فقط إذا كان هناك رصيد متبقي
      if (studentFeeStatus.value.remaining > 0 && !isEdit.value) {
        form.value.amount = Math.min(form.value.amount || 0, studentFeeStatus.value.remaining);
      } else {
        form.value.amount = 0;
      }
    } else {
      studentFeeStatus.value = { class_fee: 0, paid: 0, remaining: 0, enrollment: null, existing_discount: null };
    }
    
  } catch (e) {
    console.error("Error fetching fee status:", e);
    studentFeeStatus.value = { class_fee: 0, paid: 0, remaining: 0, enrollment: null, existing_discount: null };
  }
};

// إضافة هذه الـ watch للتأكد من تحديث البيانات عند تغيير السنة
watch(
  () => form.value.academic_year_id,
  async (newYearId) => {
    if (newYearId) {
      selectedAcademicYear.value = academicYears.value.find(y => y.id === newYearId);
      
      if (form.value.student_id) {
        await fetchStudentFeeStatus(form.value.student_id, newYearId);
      }
    }
  },
  { immediate: true }
);

const fetchReceiptData = async () => {
  if (!isEdit.value) return;

  try {
    const response = await api.get(`/payment-receipts/${route.params.id}`);
    const receipt = response.data.data;

    form.value = {
      guardian_id: receipt.guardian_id,
      student_id: receipt.student_id,
      amount: receipt.debit,
      notes: receipt.notes || "",
      academic_year_id: receipt.academic_year_id || "",
    };

    originalAmount.value = receipt.debit;

    const guardian = guardians.value.find((g) => g.id === receipt.guardian_id);
    if (guardian) {
      guardianSearch.value = `${guardian.username} - ${guardian.phone_num}`;
    }

    await fetchStudentFeeStatus(receipt.student_id, receipt.academic_year_id);
    selectedAcademicYear.value = academicYears.value.find(y => y.id === receipt.academic_year_id);

    if (receipt.guardian_id) {
      try {
        const walletRes = await api.get(
          `/guardians/${receipt.guardian_id}/wallet-balance`
        );
        walletBalance.value = walletRes.data.balance;
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
        walletBalance.value = 0;
      }
    }
  } catch (error) {
    notifyError(error, "receipts.receipt_failed");
    console.error("Error fetching receipt:", error);
  }
};

const validateAmount = () => {
  const amount = parseFloat(form.value.amount);

  if (isNaN(amount) || amount <= 0) {
    amountError.value = t("receipts.amount_required");
    return false;
  }

  if (!isEdit.value && amount > studentFeeStatus.value.remaining) {
    amountError.value = t("receipts.amount_exceeds_balance");
    return false;
  }

  amountError.value = "";
  return true;
};

const setFullAmount = () => {
  if (studentFeeStatus.value.remaining > 0) {
    form.value.amount = studentFeeStatus.value.remaining;
    validateAmount();
  }
};

const submitForm = async () => {
  isSubmitting.value = true;
  errors.value = {};
  amountError.value = "";

  try {
    if (!validateAmount()) {
      throw new Error(t("receipts.invalid_amount"));
    }

    const amount = parseFloat(form.value.amount);

    if (isEdit.value) {
      const change = amount - originalAmount.value;
      if (change > 0 && walletBalance.value < change) {
        throw new Error(t("receipts.insufficient_balance_for_increase"));
      }
    } else {
      if (walletBalance.value < amount) {
        throw new Error(t("receipts.insufficient_balance"));
      }
    }

    const payload = {
      guardian_id: form.value.guardian_id,
      student_id: form.value.student_id,
      amount: amount,
      notes: form.value.notes,
      academic_year_id: form.value.academic_year_id,
      payment_method: form.value.payment_method || null,
      check_number: form.value.check_number || null,
      payment_date: form.value.payment_date || null,
    };

    if (isEdit.value) {
      await api.put(`/payment-receipts/${route.params.id}`, payload);
      toast.success(t("receipts.edit_success"));
    } else {
      await api.post("/payment-receipts", payload);
      toast.success(t("receipts.add_success"));
    }

    if (form.value.guardian_id) {
      try {
        const walletRes = await api.get(
          `/guardians/${form.value.guardian_id}/wallet-balance`
        );
        walletBalance.value = walletRes.data.balance || 0;
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
      }
    }

    if (form.value.student_id && form.value.academic_year_id) {
      await fetchStudentFeeStatus(form.value.student_id, form.value.academic_year_id);
    }

    router.push({ name: "payment-receipts.index" });
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t("auth.forbidden", "Forbidden"));
    } else {
      if (error.response?.data?.errors) {
        errors.value = error.response.data.errors;
      }
      const msg =
        error.response?.data?.message ||
        error.message ||
        t("receipts.save_failed");
      toast.error(msg);

      if (
        msg.toLowerCase().includes("amount") ||
        msg.toLowerCase().includes("balance")
      ) {
        amountError.value = msg;
      }
    }
  } finally {
    isSubmitting.value = false;
  }
};

const fetchGuardians = async () => {
  try {
    const response = await api.get("/guardians/primary/list");
    guardians.value = response.data.data;
    if (isEdit.value) {
      await fetchReceiptData();
    }
  } catch (error) {
    notifyError(error, "receipts.guardians_failed");
  }
};

const filterGuardians = () => {
  if (!guardianSearch.value) {
    filteredGuardians.value = [];
    return;
  }
  const searchTerm = guardianSearch.value.toLowerCase();
  filteredGuardians.value = guardians.value.filter(
    (guardian) =>
      guardian.username.toLowerCase().includes(searchTerm) ||
      guardian.phone_num.includes(searchTerm)
  );
};

const selectGuardian = (guardian) => {
  form.value.guardian_id = guardian.id;
  guardianSearch.value = `${guardian.username} - ${guardian.phone_num}`;
  filteredGuardians.value = [];
};

const onStudentChange = async () => {
  if (form.value.student_id && form.value.academic_year_id) {
    await fetchStudentFeeStatus(form.value.student_id, form.value.academic_year_id);
  }
};

const onAcademicYearChange = async () => {
  selectedAcademicYear.value = academicYears.value.find(y => y.id === form.value.academic_year_id);
  
  if (form.value.student_id && form.value.academic_year_id) {
    await fetchStudentFeeStatus(form.value.student_id, form.value.academic_year_id);
  }
};

watch(
  () => form.value.guardian_id,
  async (guardianId) => {
    if (!guardianId) {
      students.value = [];
      walletBalance.value = 0;
      return;
    }
    
    try {
      loadingStudents.value = true;
      const studentsResponse = await api.get(
        `/guardians/${guardianId}/students`
      );
      students.value = studentsResponse.data.data;
    } catch (error) {
      students.value = [];
      notifyError(error, "receipts.data_failed");
    } finally {
      loadingStudents.value = false;
    }
  }
);

watch(
  () => form.value.guardian_id,
  async (guardianId) => {
    if (!guardianId) {
      walletBalance.value = 0;
      return;
    }
    
    try {
      const walletRes = await api.get(
        `/guardians/${guardianId}/wallet-balance`
      );
      walletBalance.value = walletRes.data.balance;
    } catch (error) {
      walletBalance.value = 0;
      notifyError(error, "receipts.wallet_failed");
    }
  }
);

watch(
  () => form.value.student_id,
  async (studentId) => {
    if (studentId && form.value.academic_year_id) {
      await fetchStudentFeeStatus(studentId, form.value.academic_year_id);
    }
  }
);

watch(
  () => form.value.academic_year_id,
  async (academicYearId) => {
    if (form.value.student_id && academicYearId) {
      await fetchStudentFeeStatus(form.value.student_id, academicYearId);
    }
  }
);

watch(
  () => form.value.amount,
  () => {
    validateAmount();
  }
);

watch(
  () => studentFeeStatus.value.remaining,
  () => {
    validateAmount();
  }
);

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return "0 " + t("receipts.lyd_abbr");
  }
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) {
    return "0 " + t("receipts.lyd_abbr");
  }
  return Math.round(numericAmount).toLocaleString() + " " + t("receipts.lyd_abbr");
};

onMounted(() => {
  fetchGuardians();
  loadAcademicYears();
});
</script>

<style scoped>
.guardian-search-results {
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
}
.guardian-search-results a {
  cursor: pointer;
}
.guardian-search-results a:hover {
  background-color: #f5f5f5;
}
</style>